import {
  useState,
  useRef,
  useEffect,
  MouseEventHandler,
  KeyboardEvent,
  FocusEvent,
  ChangeEvent,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { MFASend, MFALoginSend } from "pwapi";

import {
  mfaGetCode,
  mfaVerifyCode,
  resetErrors,
  resetAuth,
  mfaLoginGetCode,
  mfaSelectMethod,
} from "../actions/authActions";
import { usePerformAfterInactivity } from "../hooks/useInteractionListener";
import { AuthState } from "../reducers/authReducer";
import { RequestStatusesState } from "../reducers/requestStatusesReducer";

export const mfaMethodSentByText = {
  sms: "sent to you via SMS",
  email: "sent to you via email",
  app: "provided by your Authenticator App",
};

export type mfaMethodSelectionFormProps = {
  required?: boolean;
  onCancel?: MouseEventHandler<HTMLElement>;
  onConfirm?: MouseEventHandler<HTMLElement> | any;
};

export const useMfaMethodSelectionForm = ({
  onConfirm,
}: mfaMethodSelectionFormProps) => {
  const dispatch = useDispatch();
  const [mfaMethod, setMfaMethod] = useState<null | MFASend.mfa_method>(null);
  const authErrors = useSelector(
    ({ auth }: { auth: AuthState }) => auth.errors || {}
  );
  const mfaAppUri = useSelector(
    ({ auth }: { auth: AuthState }) => auth.mfa_app_uri
  );
  const currentMfaMethod = useSelector(
    ({ auth }: { auth: AuthState }) => auth.user?.userprofile?.mfa_method
  );
  const hasEmail = useSelector(
    ({ auth }: { auth: AuthState }) => !!auth.user?.email || auth.has_email
  );
  const hasPhone = useSelector(
    ({ auth }: { auth: AuthState }) =>
      !!auth.user?.userprofile?.phone || auth.has_phone
  );
  const [code, setCode] = useState("");

  const handleSetMfaMethod = (newMfaMethod: null | MFASend.mfa_method) => {
    if (authErrors.detail) dispatch(resetErrors());
    setCode("");
    setMfaMethod(newMfaMethod);

    if (newMfaMethod) dispatch(mfaGetCode(newMfaMethod));
  };

  const handleSubmit = (ev: any) => {
    if (ev && ev.preventDefault) ev.preventDefault();

    if (mfaMethod) dispatch(mfaVerifyCode(mfaMethod, code));
  };

  const resendLoading = useSelector(
    ({ requestStatuses }: { requestStatuses: RequestStatusesState }) =>
      requestStatuses.loading["mfa-get-code"]
  );
  const verifyLoading = useSelector(
    ({ requestStatuses }: { requestStatuses: RequestStatusesState }) =>
      requestStatuses.loading["mfa-verify-code"]
  );
  const [canResend, setCanResend] = useState(!resendLoading);
  const canResendTimeoutRef = useRef<any>(null);

  useEffect(() => {
    if (canResendTimeoutRef.current) {
      clearTimeout(canResendTimeoutRef.current);
      canResendTimeoutRef.current = null;
    }
    if (!resendLoading) {
      canResendTimeoutRef.current = setTimeout(() => {
        setCanResend(true);
      }, 10 * 1000); // Only allow resending every 10 seconds
    } else {
      setCanResend(false);
    }

    return () => {
      if (canResendTimeoutRef.current) {
        clearTimeout(canResendTimeoutRef.current);
      }
    };
  }, [resendLoading]);

  const initialMfaMethodRef = useRef(currentMfaMethod);
  useEffect(() => {
    if (initialMfaMethodRef.current !== currentMfaMethod) {
      if (onConfirm) onConfirm();
    }
  }, [currentMfaMethod]);

  const onChangeCode = (value: string) => {
    setCode(value);
  };

  useEffect(() => {
    if (!code) return;
    if (authErrors.detail) dispatch(resetErrors());
    if (code.length === 6) {
      handleSubmit({});
    }
  }, [code]);

  useEffect(() => {
    if (authErrors?.detail) {
      setCode("");
    }
  }, [authErrors?.detail]);

  return {
    mfaMethod,
    authErrors,
    mfaAppUri,
    currentMfaMethod,
    hasEmail,
    hasPhone,
    code,
    handleSetMfaMethod,
    canResend,
    onChangeCode,
    mfaMethodSentByText,
    disabled: verifyLoading,
  };
};

export type mfaLoginFormProps = {
  isSubmitting?: boolean;
  errors?: { [key: string]: any };
  touched: { [key: string]: boolean };
  values: { [key: string]: string | undefined };
  handleChange?: any;
  handleSubmit: any;
  handleReset: any;
  setFieldValue: any;
};

export const useMfaLoginForm = ({
  touched,
  values,
  handleReset,
  handleSubmit,
  setFieldValue,
}: mfaLoginFormProps) => {
  const dispatch = useDispatch();
  const authErrors = useSelector(
    ({ auth }: { auth: AuthState }) => auth.errors || {}
  );
  const mfaCodeRequired = useSelector(
    ({ auth }: { auth: AuthState }) => auth.mfa_code_required
  );
  const mfaMethod = useSelector(
    ({ auth }: { auth: AuthState }) => auth.mfa_method
  ) as MFASend.mfa_method;

  const sendMfaCode = () => {
    if (values.username) {
      dispatch(
        mfaLoginGetCode(mfaMethod as MFALoginSend.mfa_method, values.username)
      );
    }
  };

  const usernameRef = useRef(values.username);
  const passwordRef = useRef(values.password);
  const mfaMethodRef = useRef(mfaMethod);
  useEffect(() => {
    usernameRef.current = values.username;
    passwordRef.current = values.password;
    mfaMethodRef.current = mfaMethod;
  }, [values.username, values.password, mfaMethod]);
  usePerformAfterInactivity(() => {
    if (usernameRef.current || passwordRef.current || mfaMethodRef.current) {
      reset();
    }
  }, 2 * 60 * 1000); // reset after two minutes of inactivity

  const reset = () => {
    dispatch(resetAuth());
    handleReset();
  };

  useEffect(() => {
    if (["email", "sms"].includes(mfaMethod)) {
      sendMfaCode();
    }
  }, [mfaMethod]);

  const resendLoading = useSelector(
    ({ requestStatuses }: { requestStatuses: RequestStatusesState }) =>
      requestStatuses.loading["mfa-get-code"]
  );
  const [canResend, setCanResend] = useState(!resendLoading);
  const canResendTimeoutRef = useRef<any>(null);

  const selectMfaMethod = (newMfaMethod: MFALoginSend.mfa_method) => {
    setFieldValue("mfa_code", undefined);
    setFieldValue("mfa_method", newMfaMethod);
    dispatch(mfaSelectMethod(newMfaMethod));
    dispatch(resetErrors());
  };

  useEffect(() => {
    if (canResendTimeoutRef.current) {
      clearTimeout(canResendTimeoutRef.current);
      canResendTimeoutRef.current = null;
    }
    if (!resendLoading) {
      canResendTimeoutRef.current = setTimeout(() => {
        setCanResend(true);
      }, 10 * 1000); // Only allow resending every 10 seconds
    } else {
      setCanResend(false);
    }

    return () => {
      if (canResendTimeoutRef.current) {
        clearTimeout(canResendTimeoutRef.current);
      }
    };
  }, [resendLoading]);

  useEffect(() => {
    if (!values.mfa_code) return;
    if (authErrors.detail) dispatch(resetErrors());
    if (values.mfa_code.length === 6) {
      handleSubmit({});
    }
  }, [values.mfa_code]);

  useEffect(() => {
    if (authErrors?.detail) {
      setFieldValue("mfa_code", "");
    }
  }, [authErrors?.detail]);

  return {
    authErrors,
    mfaCodeRequired,
    mfaMethod,
    sendMfaCode,
    reset,
    canResend,
    selectMfaMethod,
    mfaMethodSentByText,
  };
};

export type MFACodeInputProps = {
  onChange: Function;
  value: string;
  disabled?: boolean;
};

export const useMFACodeInput = ({ onChange, value }: MFACodeInputProps) => {
  const inputRefs = [...Array(6)].map(() => useRef<HTMLInputElement>(null));

  useEffect(() => {
    if (value.length < inputRefs.length) {
      inputRefs[value.length].current?.focus();
    } else {
      inputRefs[inputRefs.length - 1].current?.blur();
    }
  }, [value]);

  const onKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key !== "Backspace") return;

    onChange(value.substring(0, value.length - 1));
  };

  const onFocus = (ev: FocusEvent<HTMLInputElement>, i: number) => {
    if (i > value.length) {
      inputRefs[value.length].current?.focus();
    }
    if (i < value.length) {
      onChange(value.substring(0, i));
    }
  };

  const onInputChange = (ev: ChangeEvent<HTMLInputElement>, i: number) => {
    ev.preventDefault();
    // Remove non-numeric characters from beginning and end of input
    const strValue = (inputRefs[i].current?.value || "")
      .replace(/^[^0-9]*/, "")
      .replace(/[^0-9]*$/, "");
    const intValue = parseInt(strValue);

    // Make sure that numeric input is a real number within bounds.
    if (isNaN(intValue) || intValue < 0 || intValue > 999999) return;
    // Make sure that there were no extra characters within pasted numeric values
    if ((strValue.replace(/^0*/, "") || "0") !== `${intValue}`) return;

    if (strValue.length === 6) {
      onChange(strValue);
    } else if (strValue.length === 1) {
      onChange(value.substring(0, i) + strValue + value.substring(i + 1));
    }
  };

  return {
    inputRefs,
    onKeyDown,
    onFocus,
    onInputChange,
  };
};
